/**
 * "Assinar com o celular" — QR code do link pessoal do signatário.
 *
 * É o caminho de quem está na recepção: a atendente mostra o QR na tela, o
 * paciente aponta a câmera e a assinatura abre no celular dele. Evita passar o
 * computador da clínica para a mão do paciente.
 *
 * O SVG do QR vem pronto do backend (lib `qrcode`), então não há dependência de
 * geração de QR no bundle do frontend.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/LinkRounded';
import PhoneIcon from '@mui/icons-material/PhoneIphoneRounded';
import type { SignerLink } from '../../types/signature';

interface SignOnMobileDialogProps {
  open: boolean;
  onClose: () => void;
  link: SignerLink | null;
  loading?: boolean;
  error?: string | null;
}

export default function SignOnMobileDialog({
  open,
  onClose,
  link,
  loading = false,
  error = null,
}: SignOnMobileDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // Contexto sem clipboard (http): o link fica visível para cópia manual.
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pr: 6 }}>
          Assinar com o celular
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.disabled' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 3 }}>
          {loading && (
            <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Typography sx={{ fontSize: 14, color: 'error.main', py: 3, textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          {!loading && !error && link && (
            <Stack spacing={2.5}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.6 }}>
                Escaneie o QR code com a câmera para abrir direto na tela de assinatura de{' '}
                <strong>{link.name}</strong>.
              </Typography>

              <Box
                sx={{
                  bgcolor: '#F1F5F9',
                  borderRadius: 2,
                  p: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {link.qr_svg ? (
                  <Box
                    sx={{ '& svg': { display: 'block', width: 200, height: 200 } }}
                    dangerouslySetInnerHTML={{ __html: link.qr_svg }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
                    QR indisponível. Use o link abaixo.
                  </Typography>
                )}
              </Box>

              <Stack spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PhoneIcon />}
                  onClick={() => window.open(link.url, '_blank', 'noopener')}
                  sx={{ textTransform: 'none', borderRadius: 2, py: 1.2, fontWeight: 600 }}
                >
                  Abrir link nesta tela
                </Button>
                <Button
                  fullWidth
                  startIcon={<LinkIcon />}
                  onClick={handleCopy}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Copiar link
                </Button>
              </Stack>

              <Typography
                sx={{
                  fontSize: 11.5,
                  color: 'text.disabled',
                  wordBreak: 'break-all',
                  textAlign: 'center',
                }}
              >
                {link.url}
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Link copiado"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
